import { useCallback, useState } from "react";
import { useEditorStore } from "../../../lib/stores/editor-store/editor-store";
import { Input } from "@/lib/components/Input";
import { Button } from "@/lib/components/Button/Button";
import { IconCheck } from "@tabler/icons-react";
import { useIsMobile } from "@/lib/use-mobile";
import { cn } from "@/lib/utils";

export default function VideoName() {
  const isMobile = useIsMobile();
  const updateIsEditingVideoName = useEditorStore(
    (state) => state.service.updateIsEditingVideoName,
  );
  const isEditingVideoName = useEditorStore(
    (state) => state.isEditingVideoName,
  );
  const videoName = useEditorStore((state) => state.video?.name ?? "");

  if (isMobile) {
    return null;
  }

  if (isEditingVideoName) {
    return (
      <VideoNameInput
        initialValue={videoName}
        onStopEditing={() => updateIsEditingVideoName(false)}
      />
    );
  }

  return (
    <button
      onClick={() => updateIsEditingVideoName(true)}
      className="flex items-center gap-2"
    >
      <h1
        className={cn(
          "inline-block cursor-text py-3 text-sm",
          videoName
            ? "font-semibold text-text-primary"
            : "font-medium text-text-secondary",
        )}
      >
        {videoName || "Type your video title here"}
      </h1>
    </button>
  );
}

type VideoNameInputProps = {
  initialValue: string;
  onStopEditing: () => void;
};

function VideoNameInput({ initialValue, onStopEditing }: VideoNameInputProps) {
  const updateVideo = useEditorStore((state) => state.service.updateVideo);
  const [name, setName] = useState(initialValue);

  const handleSubmit = useCallback(() => {
    updateVideo((video) => (video.name = name));
    onStopEditing();
  }, [name, onStopEditing, updateVideo]);

  return (
    <div className="flex items-center gap-2">
      <Input
        value={name}
        autoFocus
        tabIndex={0}
        className="h-9 shrink-1 grow-0 basis-64"
        onChange={(e) => setName(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === "Enter") {
            handleSubmit();
          }
        }}
        placeholder="Video name"
      />
      <Button size="iconSm" variant="outline" onClick={handleSubmit}>
        <IconCheck />
      </Button>
    </div>
  );
}
